import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Search, RefreshCw, ChevronLeft, ChevronRight, Clock, Filter } from "lucide-react";
import api from "@/lib/api";
import { Header } from "./Schools";

const PAGE_SIZE = 25;

// ── Formatting helpers ────────────────────────────────────────────────────────
import { fmtDateTime } from "@/lib/date";

const fmt = fmtDateTime;

// Human-readable action labels
const ACTION_LABELS = {
  "user.created":       { label: "User Created",       color: "text-emerald-700 bg-emerald-50 border border-emerald-200" },
  "user.updated":       { label: "User Updated",       color: "text-blue-700 bg-blue-50 border border-blue-200" },
  "user.deleted":       { label: "User Deleted",       color: "text-red-700 bg-red-50 border border-red-200" },
  "user.status.active":    { label: "User Activated",  color: "text-emerald-700 bg-emerald-50 border border-emerald-200" },
  "user.status.inactive":  { label: "User Deactivated",color: "text-amber-700 bg-amber-50 border border-amber-200" },
  "user.status.suspended": { label: "User Suspended",  color: "text-red-700 bg-red-50 border border-red-200" },
  "password.reset":     { label: "Password Reset",     color: "text-amber-700 bg-amber-50 border border-amber-200" },
  "password.changed":   { label: "Password Changed",   color: "text-blue-700 bg-blue-50 border border-blue-200" },
  "role.changed":       { label: "Role Changed",       color: "text-purple-700 bg-purple-50 border border-purple-200" },
  "profile.updated":    { label: "Profile Updated",    color: "text-blue-700 bg-blue-50 border border-blue-200" },
};

const ActionBadge = ({ action }) => {
  const cfg = ACTION_LABELS[action] || { label: action.replace(/\./g, " → "), color: "text-gray-700 bg-gray-100 border border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => onChange(page - 1)} disabled={page === 1}
        className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page - 3 + i;
        if (p < 1 || p > totalPages) return null;
        return (
          <button key={p} onClick={() => onChange(p)}
            className={`w-8 h-8 grid place-items-center rounded-lg text-sm ${p === page ? "bg-[#0F172A] text-white" : "border border-gray-200 hover:bg-gray-50"}`}>
            {p}
          </button>
        );
      })}
      <button onClick={() => onChange(page + 1)} disabled={page === totalPages}
        className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AuditLog() {
  const [logs, setLogs]   = useState([]);
  const [users, setUsers] = useState([]); // for name lookup
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDate, setFilterDate]     = useState("");
  const [page, setPage] = useState(1);

  // Load audit log + user list for name resolution
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes] = await Promise.allSettled([
        api.get("/admin/audit-log", { params: { limit: 500 } }),
        api.get("/admin/users"),
      ]);
      if (logsRes.status === "fulfilled") setLogs(logsRes.value.data || []);
      else toast.error("Could not load audit log.");
      if (usersRes.status === "fulfilled") setUsers(usersRes.value.data || []);
    } catch { toast.error("Load failed."); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => setPage(1), [search, filterAction, filterDate]);

  // User name lookup map
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const userName = (id) => userMap[id]?.name || (id ? id.slice(0, 8) + "…" : "System");
  const userEmail = (id) => userMap[id]?.email || "";

  // Unique actions for filter dropdown
  const uniqueActions = useMemo(() => [...new Set(logs.map((l) => l.action))].sort(), [logs]);

  // Filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return logs.filter((l) => {
      const matchQ = !q ||
        l.action?.toLowerCase().includes(q) ||
        l.detail?.toLowerCase().includes(q) ||
        userName(l.actor_id).toLowerCase().includes(q) ||
        userName(l.target_id).toLowerCase().includes(q);
      const matchA = !filterAction || l.action === filterAction;
      const matchD = !filterDate   || (l.created_at || "").startsWith(filterDate);
      return matchQ && matchA && matchD;
    });
  }, [logs, search, filterAction, filterDate, userMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageLogs   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="fade-up">
      <Header
        title="Audit Log"
        subtitle={`${logs.length} event${logs.length !== 1 ? "s" : ""} recorded`}
        actions={
          <button onClick={load} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search action, user, detail…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] text-sm" />
        </div>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white min-w-[180px]">
          <option value="">All actions</option>
          {uniqueActions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>)}
        </select>
        <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm bg-white" />
        {(search || filterAction || filterDate) && (
          <button onClick={() => { setSearch(""); setFilterAction(""); setFilterDate(""); }}
            className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-gray-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-400 text-sm">Loading audit log…</div>
        ) : pageLogs.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No events match your filters.</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3">Date / Time</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Performed By</th>
                <th className="text-left px-4 py-3">Affected User</th>
                <th className="text-left px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pageLogs.map((log) => {
                const isSelfAction = log.actor_id === log.target_id;
                return (
                  <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(log.created_at)}</td>
                    <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A] text-xs">{userName(log.actor_id)}</div>
                      {userEmail(log.actor_id) && <div className="text-xs text-gray-400">{userEmail(log.actor_id)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {isSelfAction ? (
                        <span className="text-xs text-gray-400 italic">Self</span>
                      ) : (
                        <>
                          <div className="font-medium text-[#0F172A] text-xs">{userName(log.target_id)}</div>
                          {userEmail(log.target_id) && <div className="text-xs text-gray-400">{userEmail(log.target_id)}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate" title={log.detail || ""}>{log.detail || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 px-1">
        <p className="text-xs text-gray-400">{filtered.length} of {logs.length} events</p>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
